import { getColumnTitle, getColumnTooltip } from '@src/utils/fieldDefinitionUtils';
import { prettifyTransactionControlTypeEnum } from '@src/utils/transactionControlTypeLabels';
import {
  AGGRID_AUTOCOLUMN_ID,
  createFieldLimitedAggFunc,
  equalityAggFunc,
  getAgGridColId,
  isFieldGroupInHierarchy,
  useDefaultColumns,
  type Column,
  type ColumnDef,
} from '@talos/kyoko';
import type { GridOptions, IAggFuncParams } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { TradeStatusCell } from '../LPSettlementBatchesGrid/LPSettlementTradesGridCellRenderers';
import { TradeIDCell } from '../LPSettlementBatchesGrid/TradeIDCell';
import type { TradeSettlementColumn } from '../types';
import { getLPSettlementEventTypeDisplayLabel } from '../utils/lpSettlementEventTypeLabels';
import type { LPSettlementReportBlotterRow } from './blotter/lpSettlementReportRow';
import { unsettledTransactionsFieldDefinitions } from './unsettledTransactionsFieldDefinitions';

const EVENT_ID_FIELD_EQUALITY_AGG_FUNC = 'eventIdFieldEquality';
const SETTLE_STATUS_EQUALITY_AGG_FUNC = 'settleStatusEquality';

function shouldAggregateSettlementSize({ rowNode }: IAggFuncParams): boolean {
  return isFieldGroupInHierarchy(rowNode, 'EventID') || (rowNode.allLeafChildren?.length ?? 0) === 1;
}

interface UseTradeSettlementColumnsParams {
  defaultColumns: TradeSettlementColumn[];
}

export const useTradeSettlementColumns = ({ defaultColumns }: UseTradeSettlementColumnsParams) => {
  const aggFuncs = useMemo<GridOptions<LPSettlementReportBlotterRow>['aggFuncs']>(
    () => ({
      [EVENT_ID_FIELD_EQUALITY_AGG_FUNC]: createFieldLimitedAggFunc('EventID', equalityAggFunc),
      [SETTLE_STATUS_EQUALITY_AGG_FUNC]: params => {
        if (
          isFieldGroupInHierarchy(params.rowNode, 'EventID') ||
          isFieldGroupInHierarchy(params.rowNode, 'TransactionType')
        ) {
          return equalityAggFunc(params);
        }
        return '';
      },
    }),
    []
  );
  const columnDefinitions = useMemo(() => {
    return new Map<string, Column>(
      compact([
        {
          type: 'marketAccount',
          field: 'MarketAccount',
          title: getColumnTitle(unsettledTransactionsFieldDefinitions.marketAccount),
          description: getColumnTooltip(unsettledTransactionsFieldDefinitions.marketAccount),
          initialWidth: 150,
          aggregate: true,
          enableRowGroup: true,
        },
        {
          type: 'asset',
          field: 'BaseLeg.Asset',
          title: 'Base Asset',
          initialHide: true,
        },
        {
          type: 'asset',
          field: 'QuoteLeg.Asset',
          title: 'Quote Asset',
          initialHide: true,
        },
        {
          type: 'date',
          field: 'SettleDeadline',
          title: getColumnTitle(unsettledTransactionsFieldDefinitions.settlementDue),
          description: getColumnTooltip(unsettledTransactionsFieldDefinitions.settlementDue),
          initialSort: '-',
          sortable: true,
          aggregate: true,
          initialAggFunc: EVENT_ID_FIELD_EQUALITY_AGG_FUNC,
          enableRowGroup: true,
        },
        {
          type: 'custom',
          field: 'SettleStatus',
          title: getColumnTitle(unsettledTransactionsFieldDefinitions.status),
          description: getColumnTooltip(unsettledTransactionsFieldDefinitions.status),
          initialWidth: 100,
          enableRowGroup: true,
          initialAggFunc: SETTLE_STATUS_EQUALITY_AGG_FUNC,
          params: {
            cellRenderer: TradeStatusCell,
          },
        },
        {
          type: 'side',
          field: 'EventSide',
          title: getColumnTitle(unsettledTransactionsFieldDefinitions.side),
          description: getColumnTooltip(unsettledTransactionsFieldDefinitions.side),
          enableRowGroup: true,
          initialAggFunc: EVENT_ID_FIELD_EQUALITY_AGG_FUNC,
        },
        {
          type: 'size',
          field: 'BaseLeg.Quantity',
          title: getColumnTitle(unsettledTransactionsFieldDefinitions.quantity),
          description: getColumnTooltip(unsettledTransactionsFieldDefinitions.quantity),
          initialWidth: 100,
          aggregate: true,
          params: {
            currencyField: 'BaseLeg.Asset',
            showInTermsOfContracts: true,
            highlightNegative: true,
            aggregateIf: shouldAggregateSettlementSize,
          },
        },
        {
          type: 'size',
          field: 'QuoteLeg.Amount',
          title: getColumnTitle(unsettledTransactionsFieldDefinitions.amount),
          description: getColumnTooltip(unsettledTransactionsFieldDefinitions.amount),
          initialWidth: 100,
          aggregate: true,
          params: {
            currencyField: 'QuoteLeg.Asset',
            highlightNegative: true,
            aggregateIf: shouldAggregateSettlementSize,
          },
        },
        {
          type: 'subAccount',
          field: 'subAccounts',
          title: getColumnTitle(unsettledTransactionsFieldDefinitions.subAccount),
          description: getColumnTooltip(unsettledTransactionsFieldDefinitions.subAccount),
          initialWidth: 150,
          enableRowGroup: true,
          initialAggFunc: EVENT_ID_FIELD_EQUALITY_AGG_FUNC,
        },
        {
          type: 'size',
          field: 'EventPrice',
          title: getColumnTitle(unsettledTransactionsFieldDefinitions.filledPrice),
          description: getColumnTooltip(unsettledTransactionsFieldDefinitions.filledPrice),
          params: { currencyField: 'QuoteLeg.Asset' },
          initialWidth: 125,
        },
        {
          type: 'date',
          field: 'EventTransactTime',
          title: getColumnTitle(unsettledTransactionsFieldDefinitions.transactTime),
          description: getColumnTooltip(unsettledTransactionsFieldDefinitions.transactTime),
          initialWidth: 150,
          initialAggFunc: EVENT_ID_FIELD_EQUALITY_AGG_FUNC,
          enableRowGroup: true,
        },
        {
          type: 'custom',
          field: 'EventID',
          title: getColumnTitle(unsettledTransactionsFieldDefinitions.eventId),
          description: getColumnTooltip(unsettledTransactionsFieldDefinitions.eventId),
          headerValueGetter: ({ column }) =>
            column?.getColId() === AGGRID_AUTOCOLUMN_ID
              ? getColumnTitle(unsettledTransactionsFieldDefinitions.transactionType)
              : getColumnTitle(unsettledTransactionsFieldDefinitions.eventId),
          initialRowGroup: true,
          initialRowGroupIndex: 0,
          params: {
            cellRenderer: TradeIDCell,
          },
          enableRowGroup: true,
        },
        {
          type: 'text',
          field: 'TradeID',
          title: getColumnTitle(unsettledTransactionsFieldDefinitions.tradeId),
          description: getColumnTooltip(unsettledTransactionsFieldDefinitions.tradeId),
          initialHide: true,
          enableRowGroup: true,
        },
        {
          type: 'text',
          field: 'TransactionType',
          title: getColumnTitle(unsettledTransactionsFieldDefinitions.transactionType),
          description: getColumnTooltip(unsettledTransactionsFieldDefinitions.transactionType),
          initialHide: true,
          enableRowGroup: true,
          initialAggFunc: EVENT_ID_FIELD_EQUALITY_AGG_FUNC,
          params: {
            getLabel: value => (value ? prettifyTransactionControlTypeEnum(value) : ''),
          },
        },
        {
          type: 'text',
          field: 'EventType',
          title: getColumnTitle(unsettledTransactionsFieldDefinitions.eventType),
          description: getColumnTooltip(unsettledTransactionsFieldDefinitions.eventType),
          initialHide: true,
          enableRowGroup: true,
          initialAggFunc: EVENT_ID_FIELD_EQUALITY_AGG_FUNC,
          params: {
            getLabel: value => getLPSettlementEventTypeDisplayLabel(value),
          },
        },
        {
          type: 'text',
          field: 'OrderID',
          title: getColumnTitle(unsettledTransactionsFieldDefinitions.orderId),
          description: getColumnTooltip(unsettledTransactionsFieldDefinitions.orderId),
          initialHide: true,
          enableRowGroup: true,
        },
        {
          type: 'text',
          field: 'DealID',
          title: getColumnTitle(unsettledTransactionsFieldDefinitions.dealId),
          description: getColumnTooltip(unsettledTransactionsFieldDefinitions.dealId),
          initialHide: true,
          enableRowGroup: true,
        },
        {
          type: 'text',
          field: 'EventExternalID',
          title: getColumnTitle(unsettledTransactionsFieldDefinitions.externalTradeId),
          description: getColumnTooltip(unsettledTransactionsFieldDefinitions.externalTradeId),
          initialHide: true,
        },
        {
          type: 'date',
          field: 'TradeDateEOD',
          title: getColumnTitle(unsettledTransactionsFieldDefinitions.tradeDateEOD),
          description: getColumnTooltip(unsettledTransactionsFieldDefinitions.tradeDateEOD),
          initialAggFunc: EVENT_ID_FIELD_EQUALITY_AGG_FUNC,
          enableRowGroup: true,
        },
      ] satisfies ColumnDef<LPSettlementReportBlotterRow>[]).map(c => [getAgGridColId(c), c])
    );
  }, []);

  const columns = useDefaultColumns(defaultColumns, columnDefinitions);
  return { columns, aggFuncs };
};
